import { HttpClient } from "@angular/common/http";
import { Inject, Injectable } from "@angular/core";
import { BehaviorSubject, combineLatest, EMPTY, Subject } from 'rxjs';
import { Notification, SyncedNotificationsData } from 'ng/src/app/notifications/notification.model';
import { Projections } from "app/shared/model/request-parameter.model";
import { HALResponse } from "../shared/model/app.model";
import { filter, map, skip } from "rxjs/operators";
import { Note } from "../notes/shared/note.model";
import { ServiceWorkerService } from "../../../../service-worker/service-worker.service";
import { AppStateService } from "../core/services/app-state.service";
import { ServiceWorkerEvents, SyncManagerEvents } from "../../../../service-worker/service-events";
import { ILocationService } from "angular";

const MAX_BADGE_COUNT = 9;
const POLL_INTERVAL = 10_000; // 10 seconds (in milliseconds)

@Injectable({
	providedIn: 'root'
})
export class NotificationService {

	private _openNotificationNote = new Subject<Note>();
	get openNotificationNote$() {
		return this._openNotificationNote.asObservable();
	}
	openNotificationNote(note: Note) {
		this._openNotificationNote.next(note);
	}

	private _notifications = new BehaviorSubject<Notification.Projections.WithLogName[]>([]);
	get notifications$() {
		return this._notifications.asObservable();
	}

	private bcm = new BellCountManager();
	readonly bellCount$ = this.bcm.observable;

	isAtEnd = false;

	private _isLoading = new BehaviorSubject(false);
	readonly isLoading$ = this._isLoading.asObservable();

	private pollInterval?: number = -1;
	get syncEnabled(): boolean {
		return this.pollInterval !== undefined && this.pollInterval !== -1;
	}

	private userId?: number;
	
	get unreadCount() {
		return this.bcm.count;
	}

	constructor(
		private http: HttpClient,
		private serviceWorkerService: ServiceWorkerService,
		@Inject('$location') private $location: ILocationService,
		appState: AppStateService
	) {
		combineLatest([
			serviceWorkerService.ready$,
			appState.currentUser$.pipe(skip(1)) //Skips initial due to user not actually being set
		])
			.subscribe(src => {
			let [ ready, user ] = src;
			if (!ready) return;
			this.userId = user?.id;
			const isSwitchedUser = user?.extendedData.switchedUser === 'true';
			if (isSwitchedUser) {
				window.clearInterval(this.pollInterval);
				this.pollInterval = undefined;
				this._notifications.next([]);
			} else {
				this.enableSync();
			}
			serviceWorkerService.sendEvent(SyncManagerEvents.CHANGE_USER, { id: user?.id, isSwitchedUser });
		});

		serviceWorkerService.ready$.subscribe(v => {
			if (v) {
				serviceWorkerService.registerForEvent<SyncedNotificationsData>(ServiceWorkerEvents.NOTIFICATION_UPDATE).subscribe(data => {
					this.onNotificationsDataUpdate(data);
				});

				serviceWorkerService.registerForEvent<Notification.Projections.WithLogName[]>(ServiceWorkerEvents.TOAST_UPDATE).subscribe(toasts => {
					if (serviceWorkerService.notificationsSupported) {
						toasts.forEach(toast => {
							const notification = NotificationService.createNotification(toast);
							serviceWorkerService.showDesktopNotification(notification[0], notification[1]);
						})
					}
				})
			}
		});
	}

	private onNotificationsDataUpdate(data: SyncedNotificationsData) {
		this._notifications.next(data.notifications);
		this.isAtEnd = data.pagingAtEnd;
		this.bcm.count = data.unreadCount;
		this._isLoading.next(false);
	}

	/**
	 * Enables polling if not already polling
	 * If already polling, does nothing
	 */
	enableSync() {
		if (!this.syncEnabled) {
			this.pollInterval = window.setInterval(() => {
				this.serviceWorkerService.sendEvent(SyncManagerEvents.REQUEST_SYNC, {
					notifications: this._notifications.value,
					pagingAtEnd: this.isAtEnd,
					unreadCount: this.bcm.count,
					userId: this.userId,
				});
			}, POLL_INTERVAL)
		}
	}

	/**
	 * Patches notification on LogBook server with provided notification's `seen` and `toastSeen` properties.
	 * @param notification notification to update on LogBook, `_links.self.href`, `seen`, and `toastSeen` properties used
	 * @param field The field to update
	 * @param direction corresponds to `propagateSeenStatus` parameter on server. Typically 'backward' is used to
	 * 					make all notifications before the provided one also have the seen and toastSeen statuses
	 */
	updateSeen(notification: Notification.Projections.WithLogName, field: 'seen' | 'toastSeen' | 'both', direction?: 'forward' | 'backward' | null) {
		// for single updates (w/o direction) increment/decrement the bellcount fast
		if (!direction) {
			notification.seen ? this.bcm.decrement() : this.bcm.increment();
		}
		
		const toPatch: any = {};
		const hasDirection = typeof direction === 'string';
		if (field === 'seen' || field === 'both') {
			toPatch.seen = notification.seen;
			if (hasDirection)
				toPatch.propagateSeenStatus = direction;
		}
		if (field === 'toastSeen' || field === 'both') {
			toPatch.toastSeen = notification.toastSeen;
			if (hasDirection)
				toPatch.propagateToastSeenStatus = direction;
		}

		this.http.patch(notification._links.self.href, toPatch).subscribe(() => {
			this.serviceWorkerService.sendEvent(SyncManagerEvents.FORCE_SYNC, {
				notifications: this._notifications.value,
				pagingAtEnd: this.isAtEnd,
				unreadCount: this.bcm.count,
				userId: this.userId
			});
		}, err => {
			// appears that the notification has been deleted
			// we need to just drop all our notifications and resync.
			if (err.status === 403)
				this.serviceWorkerService.sendEvent(SyncManagerEvents.RESET_DATA);
		});
	}

	markAllNotificationsSeen() {
		if (this.unreadCount <= 0) return;
		
		let notifications = this._notifications.value;
		notifications.forEach(n => n.seen = true);
		this.updateSeen(notifications[0], 'seen', 'backward');
		this.bcm.count = 0;
		this._notifications.next(notifications);
	}

	markAllToastsSeen() {
		let notification = this._notifications.value[0];
		if (!notification) return; // nothing to do
		notification.toastSeen = true;
		this.updateSeen(notification, 'toastSeen', 'backward');
	}

	incrementPage() {
		this._isLoading.next(true);
		this.serviceWorkerService.sendEvent(SyncManagerEvents.NEXT_PAGE);
	}

	previousNotification(fromNotificationId: number): Notification.Projections.WithLogName | undefined {
		const notifications = this._notifications.value;
		return notifications[
			notifications.findIndex(n => n.id === fromNotificationId)
			- 1
		];
	}

	nextNotification(fromNotificationId: number): Notification.Projections.WithLogName | undefined {
		const notifications = this._notifications.value;
		return notifications[
			notifications.findIndex(n => n.id === fromNotificationId)
			+ 1
		];
	}

	findById(id: number) {
		return this.http.get<HALResponse<{notifications: Notification.Projections.WithLogName[]}>>(
			'/api/notifications', {
				params: <Notification.Query> {
					id: id,
					size: 1,
					userDetails: this.userId,
					projection: Projections.Notifications.withLogName
				}
			}
		).pipe(
			map(resp => resp._embedded.notifications[0])
		);
	}

	getFromRoute() {
		const notificationId = this.$location.search()['notification'];
		if (notificationId) {
			return this.findById(notificationId).pipe(filter(n => n !== null && n !== undefined));
		} else{
			return EMPTY;
		}
	}

	private static createNotification(notification: Notification.Projections.WithLogName): [string, NotificationOptions] {
		let actions: NotificationAction[] = [
			{
				title: 'Mark Read',
				action: 'notificationRead'
			}
		];
		// Verifies alert event has a note
		if (notification.note && notification.note.hasOwnProperty('_links'))
		{
			notification.detailLink =
				`${window.location.origin}/#/note-detail?note=${encodeURI(notification.note._links.self.href.replace('{?projection}', ''))}&log=${notification.note.logId}`;

			// Adds the note detail action to the desktop notification
			actions.push(
				{
					title: 'View Note',
					action: 'openDetailNote'
				})
		}
		notification.notificationLink =
			`${window.location.origin}/#/notification-detail?notification=${notification.id}&log=${notification.logId}`;

		return [
			notification.subject,
			{
				body: `${notification.subject} | ${notification.logName}`,
				data: notification,
				icon: `assets/images/logbook-logo.png`,
				actions: actions,
				tag: notification.id.toString(),
			}
		]
	}
}

/**
 * BellCountManager wraps around a BehaviorSubject that controls the red notification count badge
 * It makes sure that the notification service has only one way to update the notification count
 * and that it will be consistent no matter where updated.
 */
class BellCountManager {
	private _bellCount = new BehaviorSubject('');
	private _count = 0;
	readonly observable = this._bellCount.asObservable();
	/**
	 * Updates interval subject, converts provided number to a string that will max out
	 * with 'MAX_BADGE_COUNT+' being saved when the provided number is greater than MAX_BADGE_COUNT
	 * @param num new count to be provided to subscribers
	 */
	set count(num: number) {
		this._count = num;
		if (num > MAX_BADGE_COUNT) {
			this._bellCount.next(`${MAX_BADGE_COUNT}+`);
		} else if (num >= 1) {
			this._bellCount.next(num.toString())
		} else {
			this._bellCount.next('');
		}
	}
	
	get count() {
		return this._count;
	}

	decrement() {
		this.count = --this._count
	}

	increment() {
		this.count = ++this._count;
	}
}